import { create } from 'zustand'
import type { SensorPacket } from '@/features/ble/types'

interface SensorDeviceState {
  latestPacket: SensorPacket
  packetCount: number
  hz: number
  lastHzTick: number
  hzCounter: number
}

interface SensorStoreState {
  latestPacket: SensorPacket | null
  packetCount: number
  hz: number
  byDevice: Record<string, SensorDeviceState>
  setLatestPacket: (sensorId: string | SensorPacket, packet?: SensorPacket) => void
  removeDevice: (sensorId: string) => void
  _lastHzTick: number
  _hzCounter: number
}

export const useSensorStore = create<SensorStoreState>((set, get) => ({
  latestPacket: null,
  packetCount: 0,
  hz: 0,
  byDevice: {},
  _lastHzTick: Date.now(),
  _hzCounter: 0,

  setLatestPacket: (sensorIdOrPacket, maybePacket) => {
    const sensorId = typeof sensorIdOrPacket === 'string' ? sensorIdOrPacket : 'default'
    const packet = typeof sensorIdOrPacket === 'string' ? maybePacket : sensorIdOrPacket
    if (!packet) return

    const now = Date.now()
    const { _lastHzTick, _hzCounter } = get()
    const elapsed = now - _lastHzTick
    const counter = _hzCounter + 1

    const existingDevice = get().byDevice[sensorId]
    const deviceLastTick = existingDevice?.lastHzTick ?? now
    const deviceElapsed = now - deviceLastTick
    const deviceCounter = (existingDevice?.hzCounter ?? 0) + 1
    const nextDeviceHz =
      deviceElapsed >= 500
        ? Math.round((deviceCounter / deviceElapsed) * 1000)
        : (existingDevice?.hz ?? 0)

    const nextByDevice: Record<string, SensorDeviceState> = {
      ...get().byDevice,
      [sensorId]: {
        latestPacket: packet,
        packetCount: (existingDevice?.packetCount ?? 0) + 1,
        hz: nextDeviceHz,
        lastHzTick: deviceElapsed >= 500 ? now : deviceLastTick,
        hzCounter: deviceElapsed >= 500 ? 0 : deviceCounter,
      },
    }

    // Recalculate Hz every 500 ms
    if (elapsed >= 500) {
      const hz = Math.round((counter / elapsed) * 1000)
      set({
        latestPacket: packet,
        packetCount: get().packetCount + 1,
        hz,
        byDevice: nextByDevice,
        _lastHzTick: now,
        _hzCounter: 0,
      })
    } else {
      set({
        latestPacket: packet,
        packetCount: get().packetCount + 1,
        byDevice: nextByDevice,
        _hzCounter: counter,
      })
    }
  },
  removeDevice: (sensorId) =>
    set((state) => {
      if (!state.byDevice[sensorId]) return state
      const byDevice = { ...state.byDevice }
      delete byDevice[sensorId]
      return { byDevice }
    }),
}))
